import { Router } from '@angular/router';
import { Directive, OnDestroy, OnInit } from '@angular/core';
import { combineLatest, Observable, of, Subject, Subscription } from 'rxjs';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, filter, map, startWith, switchMap, withLatestFrom } from 'rxjs/operators';
import { NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { DsButtonVariants, Icons, IDsBreadcrumbItem } from '@levelaccess/design-system';
import { VariationValue } from '@harnessio/ff-javascript-client-sdk/dist/types';

import { TextareaFieldContext } from '@client/app/components/common-textarea/common-textarea.component';
import { ISkilljarSignupField, ISkilljarSignupFieldOption } from '@shared/interfaces/academy-integration.interface';
import { SharedCommonUtility } from '@shared/utils/common.utility';
import { ISalesforceAccount } from '@shared/interfaces/salesforce.interface';
import { FeatureFlagCollection, revertFlag } from '@shared/interfaces/feature-flag.interface';
import {
  ITenant,
  ITenantArtificialIntelligence,
  ITenantPrivacySettings,
  ITenantWithPackageId,
} from '@shared/interfaces/tenant.interface';
import {
  $artificialIntelligence,
  $privacySettings,
  $tenant,
  CHAR_LIMITS,
  DEFAULT_DP_COMPLEXITY_COUNTS,
} from '@shared/constants/tenant';
import { Api } from '@shared/constants/api';
import { $academy } from '@shared/constants/academy';
import {
  $package,
  $packageAddon,
  basicPackagesForCFAddon,
  FunctionalAreaSetting,
  PackageAddonNames,
  PackageNames,
} from '@shared/constants/packaging';
import { IFunctionalAreaSettings, IPackage, IPackageAddon } from '@shared/interfaces/packaging.interface';
import { $salesforceAccount } from '@shared/constants/salesforce';
import { $userApiKey } from '@shared/constants/user-api-key';
import { InputFieldContext } from '../../../components/common-input/common-input.component';
import { RoutingService } from '../../../services/routing.service';
import { AppConfigService } from '../../../services/app-config.service';
import { ErrorMessageService } from '../../../services/error-message.service';
import { NotificationService } from '../../../services/notification.service';
import { TenantService } from '../../../services/tenant.service';
import { TranslateService } from '../../../translate/translate.service';
import { A11yService } from '../../../services/a11y.service';
import { PackageService } from '../../../services/package.service';
import { CustomValidators } from '../../../services/helpers/form-custom-validators';
import { NotificationPosition } from '../../../models/notification.model';
import { alertType } from '../../../constants/alert.constants';
import { AngularUtility } from '../../../utility/angular.utility';
import { FeatureFlagService } from '../../../services/feature-flag/feature-flag.service';
import { TenantRouteUtil } from '../tenant-route.util';

export enum Alerts {
  name = 'name',
  salesforceAccount = 'salesforceAccount',
  subdomain = 'subdomain',
}

export enum FormFields {
  name = 'name',
  salesforceAccount = 'salesforceAccount',
  internalNotes = 'internalNotes',
  description = 'description',
  domain = 'domain',
  pdfPagesTestLimit = 'pdfPagesTestLimit',
  monitoringPagesPerDigitalPropertyLimit = 'monitoringPagesPerDigitalPropertyLimit',
  seatLimit = 'seatLimit',
  package = 'package',
  digitalPropertyLimit = 'digitalPropertyLimit',
  dpComplexityCounts = 'dpComplexityCounts',
  userWayWebsiteLimit = 'userWayWebsiteLimit',
  scanDefaultTimeoutHours = 'scanDefaultTimeoutHours',
  packageAddons = 'packageAddons',
  externalPackageSignupField = 'externalPackageSignupField',
  artificialIntelligence = 'artificialIntelligence',
  privacySettings = 'privacySettings',
  scanningAgentDebugMode = 'scanningAgentDebugMode',
  scanOptions = 'scanOptions',
}

export const ArtificialIntelligenceOption = {
  enable_ai_feature: $artificialIntelligence.enabled,
};

interface AllocatedResources {
  digitalProperties: number;
  academySeats: number;
  pdfPagesTested: number;
}

@Directive()
export abstract class AbstractTenantComponent implements OnInit, OnDestroy {
  public readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  public readonly Icons: typeof Icons = Icons;
  public readonly $userApiKey: typeof $userApiKey = $userApiKey;
  public readonly FeatureFlagCollection: typeof FeatureFlagCollection = FeatureFlagCollection;
  public readonly revertFlag: typeof revertFlag = revertFlag;

  public get isDigitalAssetsPrivacySettingEnabled(): boolean {
    return this.featureFlagService.variation(this.FeatureFlagCollection.digitalAssetsPrivacySetting, false);
  }

  public get canToggleScanningAgentDebugMode(): boolean {
    return this.featureFlagService.variation(this.FeatureFlagCollection.scanningAgentDebugMode, false);
  }

  private showInternalNotesHideDescription$: Observable<boolean>;

  public descriptionFieldConfig$: Observable<TextareaFieldContext>;

  public tenant$: Observable<ITenantWithPackageId> | null = null;
  public tenantId: string;
  protected otherTenants$: Observable<ITenant[]>;

  public pageTitle: string;
  public subscription: Subscription = new Subscription();

  public form: UntypedFormGroup;
  public formValidationRequest: Subject<void> = new Subject();
  public errorResponseMessagesSet: Set<string>;

  public $academy: typeof $academy = $academy;
  public Api: typeof Api;
  public alertType: typeof alertType;
  public charLimits: typeof CHAR_LIMITS = CHAR_LIMITS;
  public $package: typeof $package = $package;
  public $packageAddon: typeof $packageAddon = $packageAddon;
  public FunctionalAreaSetting: typeof FunctionalAreaSetting = FunctionalAreaSetting;
  public FormFields: typeof FormFields = FormFields;
  public Alerts: typeof Alerts = Alerts;

  // configured on the backend
  public tenantBaseUrl: string = '';

  public availableSignupFields$: Observable<ISkilljarSignupField>;

  protected latestAvailableSignupFields: ISkilljarSignupField | null = null;

  public salesforceAccounts$: Observable<ISalesforceAccount[]>;
  protected packages$: Observable<IPackage[]>;
  protected packageAddons$: Observable<IPackageAddon[]>;
  public userWayPackages: IPackage[];
  public userWayAddonId: string;
  protected hasPackageAddons: boolean;
  public allSelectedAddonIds: string[] = [];

  public abstract get displayPackages$(): Observable<IPackage[]>;

  public showSalesforceField: boolean;
  public showUserWayWebsiteLimit: boolean;
  public showDpComplexityCalculator: boolean;
  private newMonitoringPagesLimitFF: boolean;
  public artificialIntelligenceEnabled: boolean;
  public preservePackageUsageLimits: boolean;
  readonly academyIntegrationEnabled: boolean;
  public readonly tenantRoutePath: string;

  public makeAcademyOptional: boolean = false;

  public isPackageSelectionDisabled: boolean;

  public abstract doneLoading: boolean;

  public abstract errorMessage: string;

  public abstract breadcrumbs: IDsBreadcrumbItem[];

  public abstract getPdfPagesTestInputContext(): InputFieldContext;

  public abstract submit(): Promise<void>;

  public abstract onClose(): void;

  public abstract searchSalesforceAccounts: (term$: Observable<string>) => Observable<ISalesforceAccount[]>;

  protected abstract getOtherTenants$(): Observable<ITenant[]>;

  // fields only for create
  public domainFieldEditable: boolean = false;

  // fields only for edit
  public tenantCreatedAt: string | null = null;
  public personalizedSubdomain: string = '';
  public selectedSalesforceAccount: ISalesforceAccount = null;
  public allocatedResources: AllocatedResources = { academySeats: 0, digitalProperties: 0, pdfPagesTested: 0 };

  protected currentAvailableAddons: IPackageAddon[] = [];

  protected constructor(
    protected routingService: RoutingService,
    protected formBuilder: UntypedFormBuilder,
    protected appConfigService: AppConfigService,
    protected errorMessageService: ErrorMessageService,
    protected notificationService: NotificationService,
    protected tenantService: TenantService,
    protected translateService: TranslateService,
    protected a11yService: A11yService,
    protected packageService: PackageService,
    protected featureFlagService: FeatureFlagService,
    protected router: Router,
  ) {
    this.tenantBaseUrl = this.appConfigService.getTenantBaseUrl();
    this.Api = Api;
    this.alertType = alertType;
    this.errorResponseMessagesSet = new Set<string>();
    this.isPackageSelectionDisabled = false;
    this.showUserWayWebsiteLimit = false;
    this.showDpComplexityCalculator = true;
    this.preservePackageUsageLimits = false;
    this.academyIntegrationEnabled = !this.appConfigService.getAcademyDisabled();
    this.tenantRoutePath = TenantRouteUtil.getTenantRoutePath(this.router.url);
    this.availableSignupFields$ = of(null);
  }

  private PackageAddonsFeatureFlagsMap(packageName: PackageNames): ReadonlyMap<PackageAddonNames, Function | undefined> {
    return new Map([
      [PackageAddonNames.Self_Manual_Testing, undefined],
      [PackageAddonNames.Live_App_Testing, undefined],
      [PackageAddonNames.Custom_Severities, undefined],
      [PackageAddonNames.Custom_Rule_Library, undefined],
      [PackageAddonNames.Scanning_Agent, undefined],
      [PackageAddonNames.UserWay_Integration, undefined],
      [PackageAddonNames.Scan_Rules_Curations, undefined],
      [PackageAddonNames.Issue_Tracking_Integrations, undefined],
      [PackageAddonNames.LevelDocs, undefined],
      [PackageAddonNames.Scanning_And_Reporting_APIs, undefined],
      [
        PackageAddonNames.Reporting_Essentials,
        (): Observable<boolean> =>
          this.featureFlagService.variation$(FeatureFlagCollection.AddonReportingEssentials, false) as Observable<boolean>,
      ],
      [
        PackageAddonNames.Common_Findings,
        (): Observable<boolean> => {
          return combineLatest([
            this.featureFlagService.variation$(FeatureFlagCollection.commonFindings, false),
            this.featureFlagService.variation$(FeatureFlagCollection.expandCommonFindingsPackages, false),
            this.featureFlagService.variation$(FeatureFlagCollection.commonFindingsBasicPackageAddon, false),
          ]).pipe(
            map(
              ([commonFindings, expandCommonFindingsPackages, commonFindingsBasicPackageAddon]: [
                boolean,
                boolean,
                boolean,
              ]): boolean => {
                if (!commonFindings) {
                  return false;
                }

                if (!expandCommonFindingsPackages) {
                  return true;
                }
                const isBasicPackageAndAddonFF: boolean =
                  basicPackagesForCFAddon.includes(packageName) && commonFindingsBasicPackageAddon;

                return isBasicPackageAndAddonFF;
              },
            ),
          );
        },
      ],
      [
        PackageAddonNames.Reporting_Program_View,
        (): Observable<VariationValue> =>
          this.featureFlagService.variation$(FeatureFlagCollection.AddonReportingProgramView, false),
      ],
    ]);
  }

  private setupForm(): void {
    const config: Record<string, UntypedFormControl | UntypedFormGroup> = {
      [FormFields.internalNotes]: new UntypedFormControl(null, [Validators.maxLength(CHAR_LIMITS[$tenant.internalNotes])]),
      [FormFields.description]: new UntypedFormControl(null, [Validators.maxLength(CHAR_LIMITS[$tenant.description])]),
      [FormFields.package]: new UntypedFormControl(null, [CustomValidators.required]),
      [FormFields.pdfPagesTestLimit]: new UntypedFormControl(null, [CustomValidators.required]),
      [FormFields.monitoringPagesPerDigitalPropertyLimit]: new UntypedFormControl(null, [CustomValidators.required]),
      [FormFields.digitalPropertyLimit]: new UntypedFormControl(null, [CustomValidators.required]),
      [FormFields.dpComplexityCounts]: new UntypedFormControl({ ...DEFAULT_DP_COMPLEXITY_COUNTS }),
      [FormFields.seatLimit]: new UntypedFormControl(null, [CustomValidators.required]),
      [FormFields.userWayWebsiteLimit]: new UntypedFormControl(null, [CustomValidators.required]),
      [FormFields.packageAddons]: this.formBuilder.control(undefined),
      [FormFields.artificialIntelligence]: this.formBuilder.control([ArtificialIntelligenceOption.enable_ai_feature]),
      [FormFields.privacySettings]: this.formBuilder.group({
        privacyObfuscationEnabled: false,
      }),
      [FormFields.scanningAgentDebugMode]: this.formBuilder.control(false),
      [FormFields.scanOptions]: this.formBuilder.group({}),
      [FormFields.scanDefaultTimeoutHours]: new UntypedFormControl(null, [
        CustomValidators.min(this.minScanTimeoutHours),
        CustomValidators.max(this.maxScanTimeoutHours),
      ]),
    };

    config[FormFields.name] = new UntypedFormControl(null, [
      CustomValidators.required,
      CustomValidators.minLength(3), // UserWay integration requires min len of 3
      Validators.maxLength(CHAR_LIMITS[$tenant.name]),
    ]);

    this.form = this.formBuilder.group(config);
  }

  private getAcademySeatLimitFloor(): number {
    if (!this.academyIntegrationEnabled || !this.makeAcademyOptional) {
      return 0;
    }
    const ctrl = this.form.get(FormFields.externalPackageSignupField);
    const valueStr = ctrl?.value?.value;

    if (SharedCommonUtility.isNullish(valueStr)) {
      return 0;
    }

    return valueStr.toLowerCase().includes('platform') ? 0 : 1;
  }

  private modifyUsageLimitValidator(field: string, minLimit: number, maxLimit?: number): void {
    const control: AbstractControl = this.form.get(field);
    const newValidators: ValidatorFn[] = [CustomValidators.required, CustomValidators.min(minLimit)];

    if (SharedCommonUtility.notNullish(maxLimit)) {
      newValidators.push(CustomValidators.max(maxLimit));
    }

    control.setValidators(newValidators);
    control.updateValueAndValidity();
  }

  protected get formTenantNameAndSalesforceAccount(): {
    [$tenant.name]: string;
    [$tenant.salesforceAccount]?: ISalesforceAccount;
  } {
    if (this.showSalesforceField && SharedCommonUtility.notNullish(this.selectedSalesforceAccount)) {
      return {
        [$tenant.name]: this.form.get(FormFields.name).value.trim(),
        [$tenant.salesforceAccount]: this.selectedSalesforceAccount,
      };
    }

    return { [$tenant.name]: this.form.get(FormFields.name).value.trim() };
  }

  protected get formTenantArtificialIntelligence(): { [$tenant.artificialIntelligence]: ITenantArtificialIntelligence } | null {
    if (this.artificialIntelligenceEnabled) {
      const aiOpts: string[] = this.form.get(FormFields.artificialIntelligence)?.value ?? [];

      const allFields: $artificialIntelligence[] = Object.values(ArtificialIntelligenceOption);

      const result: ITenantArtificialIntelligence = allFields.reduce((acc: ITenantArtificialIntelligence, field: string) => {
        acc[field] = aiOpts.includes(field);
        return acc;
      }, {} as ITenantArtificialIntelligence);

      return { [$tenant.artificialIntelligence]: result };
    }

    return null;
  }

  protected get formTenantPrivacySettings(): { [$tenant.privacySettings]: ITenantPrivacySettings } | null {
    if (this.featureFlagService.variation(this.FeatureFlagCollection.digitalAssetsPrivacySetting, false)) {
      return {
        [$tenant.privacySettings]: this.form.get(FormFields.privacySettings)?.value,
      };
    }

    return null;
  }

  protected get formTenantScanningAgentDebugMode(): { [$tenant.scanningAgentDebugMode]: boolean } {
    return {
      [$tenant.scanningAgentDebugMode]: this.form.get(FormFields.scanningAgentDebugMode)?.value ?? false,
    };
  }

  protected get aiOptions(): { id: string; value: string; label: string }[] {
    return Object.entries(ArtificialIntelligenceOption).map(([key, field]: [string, string]) => ({
      id: key,
      value: field,
      label: this.translateService.instant(key),
    }));
  }

  protected handleError(message: string, response: HttpErrorResponse): void {
    const errorMessage: string = this.errorMessageService.getGlobalErrorResponse(response);
    const handledMessage: string | null = this.errorMessageService.getAppErrorResponse(response);
    const appMessage: string = this.translateService.instant(message);

    const displayedMessage: string = SharedCommonUtility.notNullish(handledMessage) ? handledMessage : errorMessage;

    const translatedMessage: string =
      displayedMessage === 'academy_integration_error_message'
        ? this.translateService.instant('academy_integration_error_message_when_updating')
        : this.translateService.instant(displayedMessage);

    this.errorResponseMessagesSet.add(this.translateService.instant(translatedMessage));

    this.a11yService.setMessage(appMessage + ': ' + translatedMessage);

    this.notificationService.error(translatedMessage, NotificationPosition.Toast, true);
  }

  public get nameInputContext(): InputFieldContext {
    return {
      label: 'tenant_name',
      field: FormFields.name,
      overrideErrors: {
        unique: this.translateService.instant('form_error_unique_name'),
      },
    };
  }

  protected alertClosed: Record<Alerts, boolean> = {
    [Alerts.name]: true,
    [Alerts.salesforceAccount]: true,
    [Alerts.subdomain]: false,
  };

  public getIncludedAmount(field: string): number | null {
    if (this.form.get(FormFields.package) && this.form.get(FormFields.package).value) {
      const packageSettings = this.form.get(FormFields.package).value[$package.settings];

      const fieldMapping: Record<string, FunctionalAreaSetting> = {
        digitalPropertyLimit: FunctionalAreaSetting.digitalPropertyLimit,
        monitoringPagesLimitWithFF: FunctionalAreaSetting.monitoringPagesLimitWithFF,
        monitoringPagesLimit: FunctionalAreaSetting.monitoringPagesLimit,
        pdfPagesLimit: FunctionalAreaSetting.pdfPagesLimit,
        academySeatsLimit: FunctionalAreaSetting.academySeatsLimit,
        userWayWebsiteLimit: FunctionalAreaSetting.userWayWebsiteLimit,
      };

      const settingKey = fieldMapping[field] || field;
      return packageSettings?.[settingKey] ?? null;
    }
    return null;
  }

  public closeAlert(alert: Alerts): void {
    this.alertClosed[alert] = true;
  }

  public isAlertOpen(alert: Alerts): boolean {
    return !this.alertClosed[alert];
  }

  protected resolveExternalPackageSignupFieldForRequest(): string | undefined {
    const selected = this.form.get(FormFields.externalPackageSignupField)?.value;
    if (SharedCommonUtility.notNullish(selected?.value)) {
      return selected.value;
    }
    if (!this.makeAcademyOptional) {
      return undefined;
    }
    const platformOption: ISkilljarSignupFieldOption | undefined = this.latestAvailableSignupFields?.options?.find(
      (opt: ISkilljarSignupFieldOption): boolean => opt.value.toLowerCase().includes('platform'),
    );
    return platformOption?.value;
  }

  protected getFilteredPackageAddons(): string[] {
    const availableAddonIds: string[] = this.currentAvailableAddons.map(
      (addon: IPackageAddon): string => addon[$packageAddon._id],
    );
    return this.allSelectedAddonIds.filter((addonId: string): boolean => availableAddonIds.includes(addonId));
  }

  public get displayedSelectedAddonIds(): string[] {
    return this.getFilteredPackageAddons();
  }

  protected buildObservables(): void {
    this.otherTenants$ = this.getOtherTenants$();

    this.packages$ = this.packageService.getPackages().pipe(
      catchError((error: HttpErrorResponse): Observable<[]> => {
        this.handleError('error_get_packages', error);
        return of([]);
      }),
      AngularUtility.shareRef(),
    );

    this.subscription.add(
      this.packageService.getUserWayPackages().subscribe((packages: IPackage[]) => {
        this.userWayPackages = packages;
      }),
    );

    this.packageAddons$ = this.form.get(FormFields.package).valueChanges.pipe(
      startWith(this.form.get(FormFields.package).value),
      switchMap((pkg: IPackage) => {
        if (SharedCommonUtility.isNullish(pkg)) {
          return of([[], {}]);
        }

        return combineLatest([
          this.packageService.getPackageAddons(pkg[$package._id]),
          this.getAddonsFeatureFlagsMap(pkg[$package.name] as PackageNames),
        ]);
      }),
      map(([packageAddons, featureFlagsMap]: [IPackageAddon[], Partial<Record<PackageAddonNames, boolean>>]): IPackageAddon[] => {
        return packageAddons.filter((addon: IPackageAddon) => featureFlagsMap[addon[$packageAddon.name]] ?? true);
      }),
      AngularUtility.shareRef(),
    );

    this.subscription.add(
      this.packageAddons$.subscribe((addons: IPackageAddon[]) => {
        const userWayAddon: IPackageAddon = addons.find(
          (addon: IPackageAddon) => addon.name === PackageAddonNames.UserWay_Integration,
        );
        this.userWayAddonId = userWayAddon ? userWayAddon[$packageAddon._id] : null;
        this.hasPackageAddons = SharedCommonUtility.notNullishOrEmpty(addons);
        this.currentAvailableAddons = addons;
      }),
    );
  }

  private getAddonsFeatureFlagsMap(packageName: PackageNames): Observable<Partial<Record<PackageAddonNames, boolean>>> {
    const addonsWithFeatureFlags: PackageAddonNames[] = Array.from(this.PackageAddonsFeatureFlagsMap(packageName).keys());

    return combineLatest(
      addonsWithFeatureFlags.map((addonName: PackageAddonNames) => {
        if (SharedCommonUtility.isNullish(this.PackageAddonsFeatureFlagsMap(packageName).get(addonName))) {
          return of(true);
        }
        return this.PackageAddonsFeatureFlagsMap(packageName).get(addonName)();
      }),
    ).pipe(
      map((featureFlagValues: boolean[]): Partial<Record<PackageAddonNames, boolean>> => {
        const featureFlagValuesMap: Partial<Record<PackageAddonNames, boolean>> = {};
        addonsWithFeatureFlags.forEach((addonName: PackageAddonNames, index: number) => {
          featureFlagValuesMap[addonName] = featureFlagValues[index];
        });
        return featureFlagValuesMap;
      }),
    );
  }

  protected setupSubscriptions(): void {
    this.addUniqueNameCheck();
  }

  private addUniqueSalesforceAccountIdCheck(): void {
    const control: AbstractControl = this.form.get(FormFields.salesforceAccount);

    const otherTenantSfAccountIds$: Observable<Set<string>> = this.otherTenants$.pipe(
      map((tenants: ITenant[]) =>
        tenants.map((tenant: ITenant) => tenant[$tenant.salesforceAccountId]).filter(SharedCommonUtility.notNullish),
      ),
      map((salesForceAccountIds: string[]) => new Set(salesForceAccountIds)),
    );

    this.subscription.add(
      control.valueChanges
        .pipe(withLatestFrom(otherTenantSfAccountIds$))
        .subscribe(([account, otherTenantSfAccountIds]: [ISalesforceAccount, Set<string>]) => {
          this.alertClosed[FormFields.salesforceAccount] =
            SharedCommonUtility.notNullish(account) && !otherTenantSfAccountIds.has(account[$salesforceAccount.id]);
        }),
    );
  }

  private addUniqueNameCheck(): void {
    const control: AbstractControl | null = this.form.get(FormFields.name);

    const otherTenantNames$: Observable<Set<string>> = this.otherTenants$.pipe(
      map((tenants: ITenant[]) => new Set(tenants.map((tenant: ITenant) => tenant[$tenant.name].toLowerCase()))),
    );

    this.subscription.add(
      control.valueChanges
        .pipe(withLatestFrom(otherTenantNames$))
        .subscribe(([tenantName, otherTenantNames]: [string, Set<string>]) => {
          this.alertClosed[FormFields.name] = !otherTenantNames.has(tenantName?.toLowerCase());
        }),
    );
  }

  public selectPackage(pack: IPackage): void {
    const packageSettings: IFunctionalAreaSettings = pack[$package.settings];
    this.showUserWayWebsiteLimit = this.userWayPackages?.some((pkg: IPackage) => pkg.name === pack.name);
    this.showDpComplexityCalculator = packageSettings[FunctionalAreaSetting.dpComplexityEnabled] ?? true;

    // When the package is changed, we want the usage limits to be validated against the minimum (included) amount
    this.updateUsageLimitValidators(pack);

    if (!this.preservePackageUsageLimits) {
      this.form.patchValue({
        [FormFields.pdfPagesTestLimit]: packageSettings[FunctionalAreaSetting.pdfPagesLimit],
        [FormFields.monitoringPagesPerDigitalPropertyLimit]: this.getMonitoringPagesLimit(pack),
        [FormFields.seatLimit]: packageSettings[FunctionalAreaSetting.academySeatsLimit],
        [FormFields.digitalPropertyLimit]: packageSettings[FunctionalAreaSetting.digitalPropertyLimit],
      });
    }

    const userWayWebsiteLimitField: AbstractControl = this.form.get(FormFields.userWayWebsiteLimit);
    if (this.showUserWayWebsiteLimit) {
      if (!SharedCommonUtility.isNullish(userWayWebsiteLimitField)) {
        userWayWebsiteLimitField.enable();
      }
      const formValue: any = userWayWebsiteLimitField?.getRawValue();
      if (!this.preservePackageUsageLimits && (SharedCommonUtility.isNullish(formValue) || Number(formValue) <= 0)) {
        this.form.patchValue({
          [FormFields.userWayWebsiteLimit]: packageSettings[FunctionalAreaSetting.userWayWebsiteLimit],
        });
      }
    } else if (!SharedCommonUtility.isNullish(userWayWebsiteLimitField)) {
      userWayWebsiteLimitField.disable();
    }
  }

  public clearPackageUsageLimits(): void {
    if (!this.preservePackageUsageLimits) {
      this.form.patchValue({
        [FormFields.digitalPropertyLimit]: 0,
      });
    }
  }

  public selectPackageAddon(addons: string[]): void {
    const availableAddonIds: string[] = this.currentAvailableAddons.map(
      (addon: IPackageAddon): string => addon[$packageAddon._id],
    );
    const preservedUnavailableAddons: string[] = this.allSelectedAddonIds.filter(
      (id: string): boolean => !availableAddonIds.includes(id),
    );
    const newSelection: string[] = [...new Set([...preservedUnavailableAddons, ...addons])];

    if (
      newSelection.length !== this.allSelectedAddonIds.length ||
      !newSelection.every((id: string): boolean => this.allSelectedAddonIds.includes(id))
    ) {
      this.form.markAsDirty();
    }

    this.allSelectedAddonIds = newSelection;

    const selectPackage: IPackage = this.form.get(FormFields.package).value;
    const packageSettings: IFunctionalAreaSettings = selectPackage[$package.settings];
    this.showUserWayWebsiteLimit =
      addons.includes(this.userWayAddonId) || this.userWayPackages?.some((pkg: IPackage) => pkg.name === selectPackage.name);
    this.updateUsageLimitValidators(selectPackage);

    const userWayWebsiteLimitField: AbstractControl = this.form.get(FormFields.userWayWebsiteLimit);
    if (this.showUserWayWebsiteLimit) {
      if (!SharedCommonUtility.isNullish(userWayWebsiteLimitField)) {
        userWayWebsiteLimitField.enable();
      }
      const formValue: any = userWayWebsiteLimitField?.getRawValue();
      if (!this.preservePackageUsageLimits && (SharedCommonUtility.isNullish(formValue) || Number(formValue) <= 0)) {
        this.form.patchValue({
          [FormFields.userWayWebsiteLimit]: packageSettings[FunctionalAreaSetting.userWayWebsiteLimit] ?? 0,
        });
      }
    } else if (!SharedCommonUtility.isNullish(userWayWebsiteLimitField)) {
      userWayWebsiteLimitField.disable();
    }
  }

  private getMonitoringPagesLimit(pack: IPackage): number {
    return this.newMonitoringPagesLimitFF
      ? pack[$package.settings][FunctionalAreaSetting.monitoringPagesLimitWithFF]
      : pack[$package.settings][FunctionalAreaSetting.monitoringPagesLimit];
  }

  private updateSeatLimit(): void {
    this.modifyUsageLimitValidator(
      FormFields.seatLimit,
      Math.max(this.allocatedResources.academySeats, this.getAcademySeatLimitFloor()),
      this.appConfigService.getMaxAcademySeatLimit(),
    );
  }

  protected updateUsageLimitValidators(pack: IPackage): void {
    this.modifyUsageLimitValidator(
      FormFields.pdfPagesTestLimit,
      Math.max(this.allocatedResources.pdfPagesTested, 0),
      this.appConfigService.getAbsolutePdfPagesTestLimit(),
    );
    this.modifyUsageLimitValidator(FormFields.monitoringPagesPerDigitalPropertyLimit, 0, this.getMaxMonitoringPagesLimit(pack));
    this.updateSeatLimit();
    this.modifyUsageLimitValidator(FormFields.digitalPropertyLimit, Math.max(this.allocatedResources.digitalProperties, 0));
    if (this.showUserWayWebsiteLimit) {
      this.modifyUsageLimitValidator(FormFields.userWayWebsiteLimit, 0);
    }

    const dpComplexityCountsControl = this.form.get(FormFields.dpComplexityCounts);
    dpComplexityCountsControl?.updateValueAndValidity();
  }

  protected setupDpComplexityCountsValidator(): void {
    const dpComplexityCountsControl = this.form.get(FormFields.dpComplexityCounts);
    const digitalPropertyLimitControl = this.form.get(FormFields.digitalPropertyLimit);

    if (dpComplexityCountsControl) {
      dpComplexityCountsControl.addValidators(
        CustomValidators.dpComplexityCountsValidator(() =>
          this.showDpComplexityCalculator ? this.form.get(FormFields.digitalPropertyLimit) : null,
        ),
      );
      dpComplexityCountsControl.updateValueAndValidity();
    }

    if (digitalPropertyLimitControl && dpComplexityCountsControl) {
      this.subscription.add(
        digitalPropertyLimitControl.valueChanges.subscribe(() => {
          dpComplexityCountsControl.updateValueAndValidity();
        }),
      );
    }
  }

  protected get maxPdfPagesTestLimit(): number {
    return this.appConfigService.getAbsolutePdfPagesTestLimit();
  }

  protected get maxMonitoringPagesPerDigitalPropertyLimit(): number {
    const selectPackage: IPackage = this.form.get(FormFields.package).value;
    return selectPackage ? this.getMaxMonitoringPagesLimit(selectPackage) : null;
  }

  protected get maxSeatLimit(): number {
    return this.appConfigService.getMaxAcademySeatLimit();
  }

  protected get minScanTimeoutHours(): number {
    return this.appConfigService.getMinScanTimeoutHours();
  }

  protected get maxScanTimeoutHours(): number {
    return this.appConfigService.getMaxScanTimeoutHours();
  }

  private getMaxMonitoringPagesLimit(pack: IPackage): number {
    if (pack.name === PackageNames.LegacyEA) {
      return 3000;
    }
    if (this.newMonitoringPagesLimitFF) {
      return this.appConfigService.getAbsoluteMonitoringPagesPerDPLimitWithFF();
    }

    return this.appConfigService.getAbsoluteMonitoringPagesPerDPLimit();
  }

  public get errorResponseMessages(): string[] {
    return [...this.errorResponseMessagesSet.values()];
  }

  public get confidentialScanningEnabled(): boolean {
    const privacySettingsFormGroup = this.form.get(FormFields.privacySettings) as UntypedFormGroup;
    return privacySettingsFormGroup?.get($privacySettings.privacyObfuscationEnabled)?.value ?? false;
  }

  public get scanningAgentDebugModeEnabled(): boolean {
    return this.form.get(FormFields.scanningAgentDebugMode)?.value ?? false;
  }

  public tenantNameFormatter(account: ISalesforceAccount): string {
    return account[$salesforceAccount.name];
  }

  public selectSalesforceAccount(event: NgbTypeaheadSelectItemEvent<ISalesforceAccount>): void {
    this.selectedSalesforceAccount = event.item;
  }

  public updateConfidentialScanning(value: boolean): void {
    const privacySettingsFormGroup = this.form.get(FormFields.privacySettings) as UntypedFormGroup;
    privacySettingsFormGroup.patchValue({ privacyObfuscationEnabled: Boolean(value) }, { emitEvent: true });
    this.form.markAsDirty();
  }

  public updateScanningAgentDebugMode(value: boolean): void {
    this.form.get(FormFields.scanningAgentDebugMode).patchValue(Boolean(value), { emitEvent: true });
    this.form.markAsDirty();
  }

  public ngOnInit(): void {
    this.setupForm();

    this.pageTitle = this.routingService.getPageTitle();

    this.setupDpComplexityCountsValidator();

    this.showInternalNotesHideDescription$ = this.featureFlagService
      .variation$(FeatureFlagCollection.showInternalNotesHideDescription, false)
      .pipe(map(Boolean), AngularUtility.shareRef());

    this.descriptionFieldConfig$ = this.showInternalNotesHideDescription$.pipe(
      map<boolean, TextareaFieldContext>((showInternalNotes: boolean) => {
        const useInternalNotes: boolean = showInternalNotes;
        return {
          label: useInternalNotes ? 'tenant_internal_notes' : 'tenant_description',
          field: useInternalNotes ? FormFields.internalNotes : FormFields.description,
          charLimit: useInternalNotes ? CHAR_LIMITS[$tenant.internalNotes] : CHAR_LIMITS[$tenant.description],
          placeholder: '',
        };
      }),
      AngularUtility.shareRef(),
    );

    this.buildObservables();
    this.setupSubscriptions();

    this.subscription.add(
      combineLatest([
        this.featureFlagService.variation$(FeatureFlagCollection.salesforceIdLookup, true),
        this.featureFlagService.variation$(FeatureFlagCollection.newPackageMonitoringLimits, false),
        this.featureFlagService.variation$(FeatureFlagCollection.artificialIntelligenceOptin, false),
        this.featureFlagService.variation$(FeatureFlagCollection.preservePackageUsageLimits, false),
        this.featureFlagService.variation$(FeatureFlagCollection.makeAcademyOptional, false),
      ]).subscribe(
        ([
          salesforceIdLookup,
          newPackageMonitoringLimits,
          artificialIntelligenceOptin,
          preservePackageUsageLimits,
          makeAcademyOptional,
        ]: [boolean, boolean, boolean, boolean, boolean]): void => {
          this.showSalesforceField = salesforceIdLookup;
          this.newMonitoringPagesLimitFF = newPackageMonitoringLimits;
          this.preservePackageUsageLimits = preservePackageUsageLimits;
          this.artificialIntelligenceEnabled = this.appConfigService.getAiEnabled() && artificialIntelligenceOptin;
          this.makeAcademyOptional = makeAcademyOptional;

          if (this.academyIntegrationEnabled) {
            this.availableSignupFields$ = this.tenantService.getAvailableSignupFields();

            if (this.makeAcademyOptional) {
              this.subscription.add(
                this.availableSignupFields$.subscribe((fields: ISkilljarSignupField | null) => {
                  this.latestAvailableSignupFields = fields;
                }),
              );
            }

            this.form.addControl(
              FormFields.externalPackageSignupField,
              new UntypedFormControl(null, makeAcademyOptional ? [] : [CustomValidators.required]),
            );

            this.subscription.add(
              this.form.get(FormFields.externalPackageSignupField).valueChanges.subscribe(() => {
                this.updateSeatLimit();
              }),
            );
          } else {
            this.availableSignupFields$ = of(null);
            this.latestAvailableSignupFields = null;
          }

          if (this.showSalesforceField) {
            this.salesforceAccounts$ = this.tenantService.getSalesforceAccounts().pipe(AngularUtility.shareRef());

            const salesforceAccountControl: UntypedFormControl = new UntypedFormControl('');
            this.form.addControl(FormFields.salesforceAccount, salesforceAccountControl);

            this.subscription.add(
              salesforceAccountControl.valueChanges.pipe(filter(SharedCommonUtility.isNullishOrEmpty)).subscribe(() => {
                this.selectedSalesforceAccount = null;
              }),
            );

            this.addUniqueSalesforceAccountIdCheck();
          }
        },
      ),
    );
  }

  public ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
